import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../api/client';
import { useNotificationDispatch } from '../../context/NotificationContext';
import { TableRowSkeleton } from '../../components/admin/TableRowSkeleton';
import Pagination from '../../components/Pagination';

// Hook para Debounce
function useDebounce(value: string, delay: number) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}

interface License {
    id: number;
    purchaseDate: string;
    purchasePrice: number;
    username: string;
    pluginName: string;
    status?: string;
    licenseIp?: string;
    userId?: number;
    pluginId?: number;
}

interface LicenseStats {
    totalLicenses: number;
    activeLicenses: number;
    revenueToday: number;
    revenueTotal: number;
}

export default function AdminLicensesPage() {
    const [licenses, setLicenses] = useState<License[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const [totalLicenses, setTotalLicenses] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const licensesPerPage = 15;

    // Form states
    const [userIdentifier, setUserIdentifier] = useState('');
    const [pluginId, setPluginId] = useState('');
    const [price, setPrice] = useState(0);
    const [formStatus, setFormStatus] = useState({ loading: false, error: '', success: '' });

    // Filtros avançados
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [filterDateStart, setFilterDateStart] = useState('');
    const [filterDateEnd, setFilterDateEnd] = useState('');
    
    // Modal de detalhes
    const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    
    // Modal de edição
    const [editingLicense, setEditingLicense] = useState<License | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    
    // Estatísticas
    const [stats, setStats] = useState<LicenseStats>({
        totalLicenses: 0,
        activeLicenses: 0,
        revenueToday: 0,
        revenueTotal: 0
    });

    const { showNotification } = useNotificationDispatch();

    const fetchStats = useCallback(async () => {
        try {
            const response = await apiClient.get('/admin/licenses/stats');
            setStats(response.data);
        } catch (err) {
            console.error('Erro ao buscar estatísticas:', err);
        }
    }, []);

    const fetchLicenses = useCallback(async () => {
        setLoading(true);
        try {
            const params: any = { 
                search: debouncedSearchTerm, 
                page: currentPage, 
                limit: licensesPerPage 
            };
            
            if (filterStatus !== 'all') params.status = filterStatus;
            if (filterDateStart) params.dateStart = filterDateStart;
            if (filterDateEnd) params.dateEnd = filterDateEnd;
            
            const response = await apiClient.get('/admin/licenses', { params });
            setLicenses(response.data.licenses || []);
            setTotalLicenses(response.data.total || 0);
        } catch (err: any) {
            showNotification(err.response?.data?.error || 'Falha ao buscar licenças.', 'error');
            setLicenses([]);
            setTotalLicenses(0);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearchTerm, currentPage, licensesPerPage, filterStatus, filterDateStart, filterDateEnd, showNotification]);

    useEffect(() => {
        fetchLicenses();
        fetchStats();
    }, [fetchLicenses, fetchStats]);

    const handleGrantLicense = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormStatus({ loading: true, error: '', success: '' });
        try {
            await apiClient.post('/admin/licenses', { userIdentifier, pluginId, price });
            setFormStatus({ loading: false, error: '', success: 'Licença concedida com sucesso!' });
            showNotification('Licença concedida com sucesso!', 'success');
            setUserIdentifier('');
            setPluginId('');
            setPrice(0);
            await fetchLicenses();
            await fetchStats();
        } catch (err: any) {
            const errorMsg = err.response?.data?.error || 'Falha ao conceder a licença.';
            setFormStatus({ loading: false, error: errorMsg, success: '' });
        }
    };

    const handleDelete = async (licenseId: number) => {
        if (window.confirm('Tem certeza que deseja excluir esta licença? Esta ação não pode ser desfeita.')) {
            try {
                await apiClient.delete(`/admin/licenses/${licenseId}`);
                showNotification('Licença excluída com sucesso!', 'success');
                fetchLicenses();
                fetchStats();
            } catch (err: any) {
                showNotification(err.response?.data?.error || 'Falha ao excluir a licença.', 'error');
            }
        }
    };

    const handleUpdateLicense = async () => {
        if (!editingLicense) return;
        
        try {
            await apiClient.put(`/admin/licenses/${editingLicense.id}`, {
                price: editingLicense.purchasePrice,
                licenseIp: editingLicense.licenseIp
            });
            showNotification('Licença atualizada com sucesso!', 'success');
            setShowEditModal(false);
            setEditingLicense(null);
            fetchLicenses();
        } catch (err: any) {
            showNotification(err.response?.data?.error || 'Falha ao atualizar a licença.', 'error');
        }
    };

    const exportToCSV = () => {
        const headers = ['ID', 'Usuário', 'Plugin', 'Preço', 'Data', 'IP', 'Status'];
        const rows = licenses.map(license => [
            license.id,
            license.username,
            license.pluginName,
            license.purchasePrice.toFixed(2),
            formatDate(license.purchaseDate),
            license.licenseIp || 'N/A',
            license.status || 'completed'
        ]);
        
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `licencas_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        showNotification('Exportação concluída!', 'success');
    };

    const formatDate = (dateString: string) => new Date(dateString).toLocaleString('pt-BR');
    const formatCurrency = (value: number) => `R$ ${value.toFixed(2).replace('.', ',')}`;

    return (
        <div className="space-y-6">
            {/* Header com gradiente */}
            <div className="relative bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-2xl p-8 shadow-xl overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute -top-24 -right-24 w-96 h-96 bg-white rounded-full"></div>
                    <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-white rounded-full"></div>
                </div>
                <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-2">
                        <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                        <h1 className="text-4xl font-extrabold text-white">Gestão de Licenças</h1>
                    </div>
                    <p className="text-purple-100 text-lg">Gerencie todas as licenças de plugins da sua loja</p>
                </div>
            </div>

            {/* Cards de Estatísticas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 hover:shadow-xl transition-all">
                    <div className="flex items-center justify-between mb-4">
                        <div className="bg-blue-100 p-3 rounded-xl">
                            <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                            </svg>
                        </div>
                        <span className="text-sm font-semibold text-green-600 bg-green-100 px-2 py-1 rounded-full">+12%</span>
                    </div>
                    <h3 className="text-gray-600 text-sm font-semibold mb-1">Total de Licenças</h3>
                    <p className="text-3xl font-extrabold text-gray-900">{stats.totalLicenses}</p>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 hover:shadow-xl transition-all">
                    <div className="flex items-center justify-between mb-4">
                        <div className="bg-green-100 p-3 rounded-xl">
                            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <span className="text-sm font-semibold text-green-600 bg-green-100 px-2 py-1 rounded-full">Ativas</span>
                    </div>
                    <h3 className="text-gray-600 text-sm font-semibold mb-1">Licenças Ativas</h3>
                    <p className="text-3xl font-extrabold text-gray-900">{stats.activeLicenses}</p>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 hover:shadow-xl transition-all">
                    <div className="flex items-center justify-between mb-4">
                        <div className="bg-yellow-100 p-3 rounded-xl">
                            <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <span className="text-sm font-semibold text-green-600 bg-green-100 px-2 py-1 rounded-full">Hoje</span>
                    </div>
                    <h3 className="text-gray-600 text-sm font-semibold mb-1">Receita Hoje</h3>
                    <p className="text-3xl font-extrabold text-gray-900">{formatCurrency(stats.revenueToday)}</p>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 hover:shadow-xl transition-all">
                    <div className="flex items-center justify-between mb-4">
                        <div className="bg-purple-100 p-3 rounded-xl">
                            <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                        </div>
                        <span className="text-sm font-semibold text-purple-600 bg-purple-100 px-2 py-1 rounded-full">Total</span>
                    </div>
                    <h3 className="text-gray-600 text-sm font-semibold mb-1">Receita Total</h3>
                    <p className="text-3xl font-extrabold text-gray-900">{formatCurrency(stats.revenueTotal)}</p>
                </div>
            </div>

            {/* Formulário de Concessão */}
            <section className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                    <div className="bg-brand-blue/10 p-2 rounded-lg">
                        <svg className="w-6 h-6 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Conceder Nova Licença</h2>
                </div>
                <form onSubmit={handleGrantLicense} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div>
                        <label htmlFor="userIdentifier" className="block text-sm font-semibold text-gray-700 mb-2">
                            Username ou Email
                        </label>
                        <input 
                            type="text" 
                            id="userIdentifier" 
                            value={userIdentifier} 
                            onChange={e => setUserIdentifier(e.target.value)} 
                            required 
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent transition-all" 
                            placeholder="Ex: joao_gamer"
                        />
                    </div>
                    <div>
                        <label htmlFor="pluginId" className="block text-sm font-semibold text-gray-700 mb-2">
                            ID do Plugin
                        </label>
                        <input 
                            type="text" 
                            id="pluginId" 
                            value={pluginId} 
                            onChange={e => setPluginId(e.target.value)} 
                            required 
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent transition-all" 
                            placeholder="ex: dynamic-shops"
                        />
                    </div>
                    <div>
                        <label htmlFor="price" className="block text-sm font-semibold text-gray-700 mb-2">
                            Preço Pago (R$)
                        </label>
                        <input 
                            type="number" 
                            id="price" 
                            value={price} 
                            onChange={e => setPrice(parseFloat(e.target.value))} 
                            step="0.01" 
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent transition-all" 
                            placeholder="0.00"
                        />
                    </div>
                    <button 
                        type="submit" 
                        disabled={formStatus.loading} 
                        className="bg-gradient-to-r from-brand-blue to-blue-600 text-white h-10 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {formStatus.loading ? (
                            <>
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Concedendo...
                            </>
                        ) : (
                            <>
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Conceder
                            </>
                        )}
                    </button>
                </form>
                {formStatus.error && (
                    <div className="mt-4 bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                        <p className="text-red-700 text-sm font-medium">{formStatus.error}</p>
                    </div>
                )}
                {formStatus.success && (
                    <div className="mt-4 bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
                        <p className="text-green-700 text-sm font-medium">{formStatus.success}</p>
                    </div>
                )}
            </section>

            {/* Filtros e Lista de Licenças */}
            <section className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                {/* Cabeçalho com Filtros */}
                <div className="p-6 border-b border-gray-200 bg-gray-50">
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <svg className="w-6 h-6 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            Licenças ({totalLicenses})
                        </h2>
                        
                        <div className="flex flex-wrap items-center gap-3">
                            {/* Busca */}
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Buscar..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent w-64"
                                />
                                <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>

                            {/* Exportar CSV */}
                            <button
                                onClick={exportToCSV}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-semibold"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Exportar CSV
                            </button>
                        </div>
                    </div>

                    {/* Filtros Avançados */}
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                        <select
                            value={filterStatus}
                            onChange={e => setFilterStatus(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                        >
                            <option value="all">Todos os Status</option>
                            <option value="completed">Completas</option>
                            <option value="pending">Pendentes</option>
                            <option value="cancelled">Canceladas</option>
                        </select>

                        <input
                            type="date"
                            value={filterDateStart}
                            onChange={e => setFilterDateStart(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                            placeholder="Data Início"
                        />

                        <input
                            type="date"
                            value={filterDateEnd}
                            onChange={e => setFilterDateEnd(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                            placeholder="Data Fim"
                        />
                    </div>
                </div>

                {/* Tabela */}
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">ID</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Usuário</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Plugin</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Preço</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Data</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">IP</th>
                                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                Array.from({ length: licensesPerPage }).map((_, i) => <TableRowSkeleton key={i} columns={7} />)
                            ) : licenses.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center text-gray-400">
                                            <svg className="w-16 h-16 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                            </svg>
                                            <p className="text-lg font-semibold">Nenhuma licença encontrada</p>
                                            <p className="text-sm">Tente ajustar os filtros ou adicionar uma nova licença</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                licenses.map(license => (
                                    <tr key={license.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-sm font-mono text-gray-900">#{license.id}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-brand-blue to-blue-600 rounded-full flex items-center justify-center">
                                                    <span className="text-white font-bold text-sm">{license.username.charAt(0).toUpperCase()}</span>
                                                </div>
                                                <div className="ml-3">
                                                    <p className="text-sm font-semibold text-gray-900">{license.username}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm text-gray-900 font-medium">{license.pluginName}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-sm font-bold text-green-600">{formatCurrency(license.purchasePrice)}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(license.purchaseDate)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">{license.licenseIp || 'N/A'}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                            <div className="flex items-center justify-center gap-2">
                                                <button
                                                    onClick={() => {
                                                        setSelectedLicense(license);
                                                        setShowDetailsModal(true);
                                                    }}
                                                    className="text-brand-blue hover:text-blue-700 transition-colors"
                                                    title="Ver detalhes"
                                                >
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setEditingLicense(license);
                                                        setShowEditModal(true);
                                                    }}
                                                    className="text-yellow-600 hover:text-yellow-700 transition-colors"
                                                    title="Editar"
                                                >
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(license.id)}
                                                    className="text-red-600 hover:text-red-700 transition-colors"
                                                    title="Excluir"
                                                >
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Paginação */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <Pagination
                        currentPage={currentPage}
                        totalItems={totalLicenses}
                        itemsPerPage={licensesPerPage}
                        onPageChange={setCurrentPage}
                    />
                </div>
            </section>

            {/* Modal de Detalhes */}
            {showDetailsModal && selectedLicense && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-brand-blue to-blue-600">
                            <div className="flex items-center justify-between">
                                <h3 className="text-2xl font-bold text-white">Detalhes da Licença</h3>
                                <button
                                    onClick={() => setShowDetailsModal(false)}
                                    className="text-white hover:text-gray-200 transition-colors"
                                >
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">ID da Licença</p>
                                    <p className="text-lg font-semibold text-gray-900">#{selectedLicense.id}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Usuário</p>
                                    <p className="text-lg font-semibold text-gray-900">{selectedLicense.username}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Plugin</p>
                                    <p className="text-lg font-semibold text-gray-900">{selectedLicense.pluginName}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Preço Pago</p>
                                    <p className="text-lg font-semibold text-green-600">{formatCurrency(selectedLicense.purchasePrice)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Data de Compra</p>
                                    <p className="text-lg font-semibold text-gray-900">{formatDate(selectedLicense.purchaseDate)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">IP da Licença</p>
                                    <p className="text-lg font-mono font-semibold text-gray-900">{selectedLicense.licenseIp || 'Não atribuído'}</p>
                                </div>
                            </div>
                        </div>
                        <div className="p-6 border-t border-gray-200 bg-gray-50">
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className="w-full bg-brand-blue text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition-all"
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Edição */}
            {showEditModal && editingLicense && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
                        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-yellow-500 to-orange-500">
                            <div className="flex items-center justify-between">
                                <h3 className="text-2xl font-bold text-white">Editar Licença</h3>
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    className="text-white hover:text-gray-200 transition-colors"
                                >
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Preço (R$)</label>
                                <input
                                    type="number"
                                    value={editingLicense.purchasePrice}
                                    onChange={e => setEditingLicense({...editingLicense, purchasePrice: parseFloat(e.target.value)})}
                                    step="0.01"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">IP da Licença</label>
                                <input
                                    type="text"
                                    value={editingLicense.licenseIp || ''}
                                    onChange={e => setEditingLicense({...editingLicense, licenseIp: e.target.value})}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                                    placeholder="Ex: 192.168.1.1"
                                />
                            </div>
                        </div>
                        <div className="p-6 border-t border-gray-200 bg-gray-50 flex gap-3">
                            <button
                                onClick={() => setShowEditModal(false)}
                                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-400 transition-all"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleUpdateLicense}
                                className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-2 rounded-lg font-semibold hover:shadow-lg transition-all"
                            >
                                Salvar Alterações
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

